import { ReactNode, SyntheticEvent } from "react";
import { useNavigate } from "react-router-dom";
import { TabContext, TabList } from "@mui/lab";
import { Stack, Tab, styled, tabClasses, tabsClasses, Box } from "@mui/material";

/**
 * Height of the RouteTabs tab bar in pixels.
 * Use this when calculating sticky offsets for child elements.
 * This value is explicitly set on the tab bar container to ensure consistency.
 */
export const ROUTE_TABS_HEIGHT = 52;

export interface RouteTab {
  /** Display label for the tab */
  label: string;
  /** Route segment value (e.g., "datasets", "files") */
  value: string;
  /** Whether the tab should be visible. Defaults to true. */
  visible?: boolean;
}

interface RouteTabsProps {
  /** Array of tab configurations */
  tabs: RouteTab[];
  /** Currently active tab value (derived from route). Use null to have no active tab. */
  currentTab: string | null;
  /** Optional actions to render on the right side of the tab bar */
  actions?: ReactNode;
  /** Content to render below the tabs */
  children: ReactNode;
  /** Whether to show the tab bar. Defaults to true. */
  showTabs?: boolean;
}

const StyledTab = styled(Tab)(({ theme }) => ({
  fontWeight: 500,
  opacity: 0.75,
  padding: 0,
  minHeight: ROUTE_TABS_HEIGHT,
  transition: "all 0.2s cubic-bezier(0.165, 0.84, 0.44, 1)",
  [`&.${tabClasses.selected}`]: {
    color: theme.palette.text.primary,
    fontWeight: 500,
    opacity: 1,
  },
}));

/**
 * A reusable tab navigation component that syncs with React Router.
 * Tabs navigate to relative routes when clicked.
 *
 * @example
 * ```tsx
 * const tabs = [
 *   { label: "Datasets", value: "datasets" },
 *   { label: "Files", value: "files" },
 *   { label: "Tables", value: "tables", visible: hasPermission },
 * ];
 *
 * <RouteTabs tabs={tabs} currentTab={currentTab}>
 *   {children}
 * </RouteTabs>
 * ```
 */
const RouteTabs = ({ tabs, currentTab, actions, children, showTabs = true }: RouteTabsProps) => {
  const navigate = useNavigate();

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    navigate(newValue);
  };

  const visibleTabs = tabs.filter((tab) => tab.visible !== false);

  // Handle explicit null case for no active tab, otherwise ensure currentTab is valid
  const getValidTabValue = (): string => {
    // When currentTab is explicitly null, use dummy value so no tab appears selected
    if (currentTab === null) {
      return "__no_active_tab__";
    }

    // If currentTab matches a visible tab, use it
    const isCurrentTabVisible = visibleTabs.some((tab) => tab.value === currentTab);
    if (isCurrentTabVisible) {
      return currentTab;
    }

    // Fallback to first visible tab or default
    return visibleTabs[0]?.value ?? "overview";
  };

  const validTabValue = getValidTabValue();

  return (
    <Stack bgcolor="common.pageBackground" sx={{ minHeight: "100%", flex: 1 }}>
      <TabContext value={validTabValue}>
        {showTabs && (
          <Stack
            direction="row"
            alignItems="stretch"
            borderBottom="1px solid"
            borderColor="divider"
            sx={{
              position: "sticky",
              px: 2,
              top: 0,
              height: ROUTE_TABS_HEIGHT,
              minHeight: ROUTE_TABS_HEIGHT,
              zIndex: 1,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "saturate(1.8) blur(20px)",
            }}
          >
            <TabList
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                borderBottom: "none",
                flex: 1,
                minWidth: 0,
                minHeight: ROUTE_TABS_HEIGHT,
                [`& .${tabsClasses.flexContainer}`]: {
                  gap: 3,
                  height: "100%",
                },
                [`& .${tabsClasses.indicator}`]: {
                  backgroundColor: "text.primary",
                  ...(currentTab === null && { display: "none" }),
                },
              }}
            >
              {/* Hidden dummy tab to satisfy MUI when currentTab is null */}
              {currentTab === null && (
                <Tab value="__no_active_tab__" sx={{ display: "none" }} disabled />
              )}
              {visibleTabs.map((tab) => (
                <StyledTab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  sx={{
                    // Prevent visual selection when no tab should be selected (currentTab is null)
                    ...(currentTab === null && {
                      '&[aria-selected="true"]': {
                        color: "inherit",
                        fontWeight: 500,
                        opacity: 0.75,
                      },
                    }),
                  }}
                />
              ))}
            </TabList>
            {actions && (
              <Box display="flex" alignItems="center" gap={1} flexShrink={0} pl={1}>
                {actions}
              </Box>
            )}
          </Stack>
        )}
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>{children}</Box>
      </TabContext>
    </Stack>
  );
};

export default RouteTabs;
